import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartContext } from '../../../context/CartContext';
import MoneyUtils from '../../../utils/moneyUtils';
import { capitalizeFirst } from '../../../utils/stringUtils';
import { listenToCartHoldStatus } from '../../../services/api/firebaseAdapter';
import confetti from 'canvas-confetti';

const CartBottomSheet = ({
  isOpen,
  onClose,
  onClearCart,
  theme,
  isGeneratingCode,
  billerCode,
  onGetCode,
  restaurantId,
  activeCartHoldId
}) => {
  const { cartData, updateQuantity, removeItem } = useCartContext();
  const [isClaimedSuccess, setIsClaimedSuccess] = useState(false);

  // Listen for real-time status changes when a cart hold is active
  useEffect(() => {
    if (!activeCartHoldId || !restaurantId || !isOpen) {
      return;
    }

    let timeoutId;

    const unsubscribe = listenToCartHoldStatus(restaurantId, activeCartHoldId, (result) => {
      if (result.success && result.data?.status === 'claimed') {
        setIsClaimedSuccess(true);
        onClearCart();
        
        // Fire small amount of confetti
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.6 },
          zIndex: 9999
        });

        // Auto close after 5 seconds
        timeoutId = setTimeout(() => {
          onClose();
        }, 5000);
      }
    });

    return () => {
      unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [activeCartHoldId, restaurantId, isOpen, onClearCart, onClose]);

  // Reset success state when bottom sheet is completely closed
  useEffect(() => {
    if (!isOpen) {
      // Small timeout to allow exit animation to finish
      setTimeout(() => setIsClaimedSuccess(false), 300);
    }
  }, [isOpen]);

  const primaryColor = theme?.primary || '#000';
  const secondaryColor = theme?.secondary || '#fff';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          />

          {/* Bottom Sheet Modal */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 bg-gray-50 rounded-t-3xl z-[70] max-h-[85vh] flex flex-col shadow-2xl"
          >
            {/* Handle/Close Header */}
            <div className="bg-white rounded-t-3xl border-b border-gray-100 flex items-center justify-between px-5 pt-4 pb-3 shrink-0">
              <h2 className="text-xl font-bold text-gray-900">{isClaimedSuccess ? 'Order Success' : 'Your Cart'}</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32">
              
              {isClaimedSuccess ? (
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex flex-col items-center justify-center py-10 space-y-6 text-center"
                >
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', damping: 12, stiffness: 100, delay: 0.1 }}
                    className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center shadow-inner"
                  >
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 mb-2">Order Claimed!</h2>
                    <p className="text-sm text-gray-500 max-w-[250px] mx-auto leading-relaxed">
                      Your order has been received by the cashier. Thanks for ordering!
                    </p>
                  </div>
                </motion.div>
              ) : (
                <>
                  {/* If code is generated, display it prominently at the top */}
              {billerCode && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex items-center justify-between animate-in fade-in zoom-in duration-300">
                  <div>
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Your Pickup Code</h3>
                    <p className="text-[10px] text-gray-400">
                      Show this to place order.
                    </p>
                  </div>
                  <div 
                    className="inline-flex px-4 py-2 rounded-xl border-2 shadow-sm"
                    style={{ backgroundColor: `${secondaryColor}15`, borderColor: secondaryColor }}
                  >
                    <span 
                      className="text-2xl font-black tracking-widest"
                      style={{ color: secondaryColor }}
                    >
                      {billerCode}
                    </span>
                  </div>
                </div>
              )}

              {/* Cart Items List */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-50 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900">Cart Items</h3>
                  {cartData?.items?.length > 0 && (
                    <button
                      onClick={() => {
                        onClearCart();
                        onClose();
                      }}
                      className="text-[10px] font-bold py-1.5 px-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors uppercase tracking-widest"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="divide-y divide-gray-50">
                  {cartData?.items?.length > 0 ? cartData.items.map(item => (
                    <div key={item.cartItemId} className="p-4 flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900 line-clamp-2">{capitalizeFirst(item.name)}</h4>
                            <p className="text-xs text-gray-500">
                                {MoneyUtils.format(
                                    (item.price || 0) + 
                                    (item.customizations?.addons || item.customizations?.addOns || []).reduce(
                                        (s, a) => s + (a.price || 0) * (a.quantity || 1), 0
                                    )
                                )}
                            </p>
                          </div>
                          <div className="flex items-center bg-white border border-gray-200 rounded-lg h-7 shadow-sm ml-2">
                            <button
                              onClick={() => item.quantity > 1 ? updateQuantity(item.cartItemId, item.quantity - 1) : removeItem(item.cartItemId)}
                              className="w-7 h-full flex items-center justify-center text-gray-500 hover:text-red-500"
                              disabled={!!billerCode}
                            >-</button>
                            <span className="w-4 text-center text-xs font-bold">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                              className="w-7 h-full flex items-center justify-center text-gray-500 hover:text-green-600"
                              disabled={!!billerCode}
                            >+</button>
                          </div>
                        </div>
                        {item.customizations && (
                          <div className="text-[10px] text-gray-400 mt-1 flex flex-col gap-0.5">
                            {item.customizations.portion && <span>Size: {capitalizeFirst(item.customizations.portion.name || item.customizations.portion.size)}</span>}
                            {(item.customizations.addons || item.customizations.addOns) && (
                              <span>
                                  Add-ons:{' '}
                                  {(item.customizations.addons || item.customizations.addOns)
                                      .map(a => `+${MoneyUtils.format(a.price || 0)} ${capitalizeFirst(a.name)}`)
                                      .join(', ')}
                              </span>
                            )}
                            {item.specialInstructions && <span className="italic">Note: {item.specialInstructions}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="p-8 text-center text-sm text-gray-500">Your cart is empty.</div>
                  )}
                </div>
              </div>

              {/* Bill Details */}
              {cartData?.items?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Bill Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Item Total</span>
                      <span>{MoneyUtils.format(cartData.subtotal)}</span>
                    </div>
                    {cartData.tax > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Taxes</span>
                        <span>{MoneyUtils.format(cartData.tax)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-100 pt-2 mt-2 flex justify-between font-bold text-gray-900">
                      <span>To Pay</span>
                      <span>{MoneyUtils.format(cartData.total)}</span>
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
            </div>

            {/* Bottom Action Bar inside Sheet */}
            <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 z-40 pb-safe px-4 pt-4 shadow-[0_-10px_40px_rgba(0,0,0,0.06)]"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
              
              {isClaimedSuccess ? (
                <button
                  onClick={onClose}
                  className="w-full py-4 rounded-xl text-sm font-black shadow-[0_8px_20px_rgba(0,0,0,0.15)] transition-all flex items-center justify-center uppercase tracking-wider text-white"
                  style={{ backgroundColor: secondaryColor }}
                >
                  Close
                </button>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col shrink-0 px-1">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none mb-1">
                      To Pay
                    </span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-black text-gray-900 leading-none">
                        {MoneyUtils.format(cartData.total)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-1 justify-end items-center gap-2 border-l pl-4 border-gray-100">
                  {!billerCode ? (
                    <button
                      onClick={onGetCode}
                      disabled={isGeneratingCode || cartData?.items?.length === 0}
                      className="w-full py-3 px-2 rounded-xl text-[11px] font-black shadow-[0_8px_20px_rgba(0,0,0,0.15)] disabled:opacity-50 transition-all flex items-center justify-center uppercase tracking-wider scale-wrapper active:scale-95"
                      style={{ backgroundColor: secondaryColor, color: primaryColor }}
                    >
                      {isGeneratingCode ? '...Wait...' : 'Get Code'}
                    </button>
                  ) : (
                    <button
                      onClick={onClose}
                      className="w-full py-3 px-2 rounded-xl text-[11px] font-black shadow-none transition-all flex items-center justify-center uppercase tracking-wider scale-wrapper active:scale-95 bg-gray-100 text-gray-900 hover:bg-gray-200"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
              )}
            </div>

          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartBottomSheet;
